import { Skeleton, Stack } from '@mui/material';
import { Button } from 'system/atoms';
import { VerticalTabs } from 'common/ui';
import { ItemSeparator } from 'common/ui/ItemSeparator';
import { TabsV2 } from 'common/ui/TabsV2';
import { TextBlock } from 'common/ui/TextBlock';
import { getCandidateProfileAnalysisTabs } from 'modules/candidate-profile-analysis/utils/getCandidateProfileAnalysisTabs';
import { useEffect, useState } from 'react';
import { CandidateProfileAnalysisTab } from 'modules/candidate-profile-analysis/utils/getCandidateProfileAnalysisTabs';
import { getCandidateProfileAnalysisComponents } from 'modules/candidate-profile-analysis/tabs/candidateProfileAnalysisComponents';
import {
  useGetCandidateByCandidateProfileQuery,
  useOnUpdateCandidateByCandidateIdSubscription
} from 'generated-graphql/hooks/apollo-hooks';
import { extractQueryGetItem } from 'core/utils/apollo/extractGetItem';
import { getCandidateProfileApplicantSeparatorItems } from 'modules/candidate-profile/utils/separator-items/getCandidateProfileApplicantSeparatorItems';
import { sanitizeObject } from 'utils/fomatting';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Paths } from 'services/navigation/paths';
import { useCandidateProfileStore } from 'modules/candidate-profile/stores/useCandidateProfileStore';
import { EmptyState } from 'system';
import { getAnalysisTitle } from 'utils/getAnalysisTitle';

const CandidateProfileApplicantSkeleton: React.FC = () => {
  return (
    <Stack gap="30px">
      <Skeleton variant="rectangular" width="500px" height={69} />
      <Stack
        direction="row"
        justifyContent="space-between"
        gap="10px"
        width="100%"
      >
        <Skeleton variant="rectangular" width="500px" height={32} />
        <Skeleton variant="rectangular" width="150px" height={32} />
      </Stack>
    </Stack>
  );
};

export const CandidateProfileApplicantsTab: React.FC = () => {
  const navigate = useNavigate();
  const { analysedApplications } = useCandidateProfileStore();
  const [selectedApplicantId, setSelectedApplicantId] = useState<string | null>(
    null
  );

  const [selectedTab, setSelectedTab] =
    useState<CandidateProfileAnalysisTab>('Details');

  useEffect(() => {
    if (selectedApplicantId) return;
    if (analysedApplications?.[0]?.id) {
      setSelectedApplicantId(analysedApplications?.[0]?.id);
    }
  }, [analysedApplications]);

  const { data, updateQuery, refetch } = useGetCandidateByCandidateProfileQuery(
    {
      variables: {
        candidateId: selectedApplicantId ?? ''
      },
      skip: !selectedApplicantId
    }
  );

  useOnUpdateCandidateByCandidateIdSubscription({
    variables: {
      candidateId: selectedApplicantId ?? ''
    },
    skip: !selectedApplicantId,
    onData: ({ data }) => {
      const candidateUpdated = data?.data?.onUpdateCandidate;
      if (!candidateUpdated) return;

      let analysisChanged = false;

      updateQuery((prev) => {
        if (!prev.getCandidate) return prev;
        analysisChanged =
          (!!prev.getCandidate.analysisErrorCode &&
            !candidateUpdated.analysisErrorCode) ||
          (candidateUpdated.totalScoreScaled != null &&
            candidateUpdated.totalScoreScaled !==
              prev.getCandidate.totalScoreScaled);
        return {
          ...prev,
          getCandidate: {
            ...prev.getCandidate,
            ...sanitizeObject(candidateUpdated),
            // nullable on purpose: a later successful analysis clears the code
            analysisErrorCode: candidateUpdated.analysisErrorCode ?? null
          }
        };
      });

      // The requirement outcomes do not ride the subscription, so a recovered
      // or re-scored candidate needs the full query again.
      if (analysisChanged) {
        void refetch().catch(() => undefined);
      }
    }
  });

  const candidate = extractQueryGetItem(data);

  const components = getCandidateProfileAnalysisComponents({
    candidate: candidate
  });

  const jobDescription = candidate?.candidateBatch?.jobDescriptionParsed;

  const separatorItems = getCandidateProfileApplicantSeparatorItems(candidate);

  if (analysedApplications?.length === 0) {
    return <EmptyState type="no-records" />;
  }

  const selectedIndex =
    analysedApplications?.findIndex(
      (applicant) => applicant.id === selectedApplicantId
    ) ?? 0;

  return (
    <Stack direction="row">
      <VerticalTabs
        containerSx={{
          height: 'fit-content'
        }}
        defaultSelectedIndex={selectedIndex >= 0 ? selectedIndex : 0}
        items={
          analysedApplications?.map((applicant) => ({
            id: applicant.id,
            title: getAnalysisTitle(applicant.candidateBatch),
            subtitle:
              applicant.candidateBatch?.jobDescriptionParsed?.employerName ??
              '',
            description:
              applicant.candidateBatch?.jobDescriptionParsed?.location ?? ''
          })) || []
        }
        onTabChange={(item) => {
          setSelectedApplicantId(item.id);
        }}
      />
      <Stack flex={1} gap="30px">
        {!candidate && <CandidateProfileApplicantSkeleton />}
        {candidate && (
          <>
            <TextBlock
              variant="section"
              title={getAnalysisTitle(candidate?.candidateBatch)}
              subtitle={jobDescription?.location ?? ''}
            />
            <Stack direction="row" justifyContent="space-between">
              <ItemSeparator items={separatorItems} />
              <Button
                variant="tertiary"
                sx={{ padding: 0 }}
                onClick={() => {
                  navigate(`${Paths.ANALYSE}/${candidate.candidateBatch?.id}`);
                }}
              >
                View analysis <ArrowForward />
              </Button>
            </Stack>
          </>
        )}
        <TabsV2
          value={selectedTab}
          tabs={getCandidateProfileAnalysisTabs({
            calendarBookingId:
              candidate?.conversation?.calendarBookingId ?? null
          })}
          onChange={(value) => {
            setSelectedTab(value as CandidateProfileAnalysisTab);
          }}
        />
        {components[selectedTab]}
      </Stack>
    </Stack>
  );
};
